import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { useSession } from '../../lib/auth/session.js';
import { tmsKeys, useQueues } from '../../lib/api/hooks-tms.js';
import { confirmAction } from '../../components/ui/Confirm.js';
import { Empty, ErrorBanner, Segmented, TableSkeleton, SectionHead, EditorHead } from '../../components/ui/Primitives.js';
import { Field } from '../../components/ui/Form.js';
import { Icon } from '../../components/ui/Icon.js';
import { OrderedPicker } from '../../components/ui/OrderedPicker.js';
import { RowAction, openRowProps } from '../../components/ui/RowAction.js';
import { useOpenItem } from '../../lib/openitem.js';
import { UnsavedGuard } from '../../components/ui/UnsavedGuard.js';
import { POOL_PERIODS, POOL_PERIOD_LABEL, type NumberPool, type NumberPoolInput, type PoolNumberOption, type PoolPeriod } from '@arcnet/contracts';

/**
 * Number pools (0137, 0138): the numbers a campaign shows as its caller ID, taking turns in order
 * or at random. Only free numbers can go in -- the account's own, on no inbound route and in no
 * other pool -- and calls back on them can go to a queue through a route the pool keeps.
 */
export function NumberPoolsTab({ tenantId }: { tenantId: string }) {
  const can = useSession((s) => s.can);
  const mayEdit = can('campaign:write');
  const qc = useQueryClient();
  const base = `/v1/tenants/${tenantId}/number-pools`;
  const key = ['number-pools', tenantId];
  const list = useQuery({ queryKey: key, queryFn: () => api.get<{ data: NumberPool[]; numbers: PoolNumberOption[] }>(base) });
  const queues = useQueues(tenantId);
  // The open pool is in the address, by its name (owner, 21 October 2026).
  const opened = useOpenItem(list.data?.data, (x) => x.name);
  const editing: NumberPool | 'new' | null = opened.creating ? 'new' : opened.item;

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: key });
    void qc.invalidateQueries({ queryKey: tmsKeys.inboundRoutes(tenantId) });
  };
  const remove = useMutation({ mutationFn: (id: string) => api.delete(`${base}/${id}`), onSuccess: invalidate });

  const pools = list.data?.data ?? [];
  const options = list.data?.numbers ?? [];
  const free = options.filter((o) => !o.route && !o.pool).length;

  // The editor takes the list's place, as on Queues (UI review, 3 October 2026).
  if (editing !== null) {
    return (
      <>
        <EditorHead back="Number pools" onBack={opened.close}
          title={editing === 'new' ? 'New number pool' : editing.name} />
        <UnsavedGuard key={editing === 'new' ? 'new' : editing.id} readOnly={!mayEdit}>
          <PoolForm base={base} pool={editing === 'new' ? null : editing}
            options={options} queues={(queues.data?.data ?? []).map((q) => ({ id: q.id, name: q.name }))}
            onDone={() => { invalidate(); opened.close(); }} onCancel={opened.close} />
        </UnsavedGuard>
      </>
    );
  }

  return (
    <>
    <SectionHead title="Number pools"
      sub="Groups of your numbers a campaign shows as its caller ID, in turn or at random."
      action={mayEdit && (
        <button type="button" className="arc-btn arc-btn-sm arc-btn-primary" onClick={opened.openNew}
          disabled={list.data !== undefined && free === 0}
          title={list.data !== undefined && free === 0 ? 'Every number is routed or in a pool. A pool takes only free numbers.' : undefined}>
          <Icon name="plus" size={13} /> New pool
        </button>
      )} />
    {remove.error && <ErrorBanner error={remove.error} />}
    <div className="arc-card">
      <div className="arc-table-wrap">
        <table className="arc-table">
          <colgroup>
            <col /><col style={{ width: 190 }} /><col style={{ width: 150 }} /><col style={{ width: 170 }} /><col style={{ width: 90 }} />
          </colgroup>
          <thead>
            <tr><th>Pool</th><th>Changes</th><th>Showing now</th><th>Calls back go to</th><th /></tr>
          </thead>
          {list.data === undefined ? <TableSkeleton query={list} cols={5} /> : (
            <tbody>
              {pools.map((p) => (
                <tr key={p.id} {...openRowProps(() => opened.open(p))}>
                  <td>
                    <div className="arc-stack" style={{ gap: 1 }}>
                      <span style={{ fontWeight: 550 }}>{p.name}</span>
                      <span className="arc-subtle" style={{ fontSize: 11.5 }}>
                        {p.numbers.length} {p.numbers.length === 1 ? 'number' : 'numbers'}
                        {p.campaigns.length > 0 && ` · ${p.campaigns.map((c) => c.name).join(', ')}`}
                      </span>
                    </div>
                  </td>
                  <td>{p.mode === 'rotate' ? 'In order' : 'At random'}, {POOL_PERIOD_LABEL[p.period].toLowerCase()}</td>
                  <td>{p.current ? <span className="arc-mono">{p.current}</span> : <span className="arc-subtle">Picked per call</span>}</td>
                  <td>{p.return_queue_name ?? <span className="arc-subtle">Not routed</span>}</td>
                  <td style={{ textAlign: 'right' }}>
                    {mayEdit && (
                      <>
                        <RowAction kind="edit" label={`Edit ${p.name}`} onClick={() => opened.open(p)} />
                        <RowAction kind="delete"
                          label={p.campaigns.length > 0
                            ? `${p.name} is used by ${p.campaigns.map((c) => c.name).join(', ')}; take it off ${p.campaigns.length === 1 ? 'that campaign' : 'those campaigns'} first`
                            : `Remove ${p.name}`}
                          disabled={p.campaigns.length > 0 || remove.isPending}
                          onClick={() => {
                            void confirmAction({ title: `Remove the pool "${p.name}"?`, action: 'Remove', danger: true })
                              .then((ok) => { if (ok) remove.mutate(p.id); });
                          }} />
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>
      {list.data !== undefined && pools.length === 0 && (
        <Empty icon="hash" title="No number pools yet"
          hint={free === 0 ? 'Every number is routed or in a pool. A pool takes only free numbers.'
            : 'Put free numbers in a pool, then choose it as a campaign’s caller ID.'} />
      )}
    </div>
    </>
  );
}

function PoolForm({ base, pool, options, queues, onDone, onCancel }: {
  base: string; pool: NumberPool | null; options: PoolNumberOption[]; queues: Array<{ id: string; name: string }>;
  onDone: () => void; onCancel: () => void;
}) {
  const [v, setV] = useState<NumberPoolInput>(pool
    ? { name: pool.name, mode: pool.mode, period: pool.period, numbers: pool.numbers, return_queue_id: pool.return_queue_id }
    : { name: '', mode: 'rotate', period: 'day', numbers: [], return_queue_id: null });
  const save = useMutation({
    mutationFn: () => pool ? api.put(`${base}/${pool.id}`, v) : api.post(base, v),
    onSuccess: onDone,
  });
  // Free numbers, and this pool's own. The rest are listed so nobody wonders where they went.
  const mine = (o: PoolNumberOption) => !o.route && (!o.pool || o.pool.id === pool?.id);
  const items = options.filter(mine).map((o) => ({ id: o.e164, label: o.e164 }));
  const taken = options.filter((o) => !mine(o));
  const periods = POOL_PERIODS.filter((p) => v.mode === 'random' || p !== 'call');
  const set = <K extends keyof NumberPoolInput>(k: K, x: NumberPoolInput[K]) => setV((s) => ({ ...s, [k]: x }));

  return (
    <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
      <div className="arc-card arc-card-body arc-stack" style={{ gap: 14, marginBottom: 14 }}>
      {save.error && <ErrorBanner error={save.error} />}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px 20px' }}>
        <Field label="Name" htmlFor="pool-name">
          <input id="pool-name" className="arc-input" value={v.name} maxLength={60} autoFocus
            onChange={(e) => set('name', e.target.value)} placeholder="Sales numbers" />
        </Field>
        <Field label="Calls back go to" htmlFor="pool-queue"
          hint={v.return_queue_id ? `Kept as the inbound route “${v.name || 'pool'} return calls”.` : 'Leave unrouted, or pick a queue.'}>
          <select id="pool-queue" className="arc-select" value={v.return_queue_id ?? ''}
            onChange={(e) => set('return_queue_id', e.target.value || null)}>
            <option value="">Not routed</option>
            {queues.map((q) => <option key={q.id} value={q.id}>{q.name}</option>)}
          </select>
        </Field>
        <Field label="Order">
          <Segmented label="Order" value={v.mode}
            options={[['rotate', 'In order'], ['random', 'At random']] as const}
            onChange={(m) => setV((s) => ({ ...s, mode: m, period: m === 'rotate' && s.period === 'call' ? 'day' : s.period }))} />
        </Field>
        <Field label="Next number" htmlFor="pool-period"
          hint={v.mode === 'random' ? 'Never the same number twice running.' : 'Days turn at the account’s midnight; weeks on Monday.'}>
          <select id="pool-period" className="arc-select" value={v.period} onChange={(e) => set('period', e.target.value as PoolPeriod)}>
            {periods.map((p) => <option key={p} value={p}>{POOL_PERIOD_LABEL[p]}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Numbers" {...(v.mode === 'rotate' ? { hint: 'Used in this order.' } : {})}>
        <OrderedPicker items={items} value={v.numbers} onChange={(n) => set('numbers', n)} mono
          emptyText={items.length ? 'Add at least one number.' : 'No free numbers.'} />
      </Field>
      {taken.length > 0 && (
        <div className="arc-subtle" style={{ fontSize: 12 }}>
          Not free: {taken.map((o) => `${o.e164} (${o.pool ? o.pool.name : `routed to ${o.route}`})`).join(', ')}. Take a number
          off its route under Inbound routes to use it here.
        </div>
      )}
      </div>
      <div className="arc-row" style={{ gap: 8 }}>
        <button type="submit" className="arc-btn arc-btn-primary"
          disabled={!v.name.trim() || v.numbers.length === 0 || save.isPending}>{save.isPending ? 'Saving…' : pool ? 'Save changes' : 'Create pool'}</button>
        <button type="button" className="arc-btn" onClick={onCancel}>Cancel</button>
        {v.numbers.length > 0 && <span className="arc-subtle">{v.numbers.length} chosen</span>}
      </div>
    </form>
  );
}
